import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  Wallet,
  Receipt,
  Package,
  Settings,
  MessageSquare,
  Link2,
  Coins,
  Megaphone,
  ShieldAlert,
  MapPin,
  Radar,
  LogOut,
  Bell,
  Menu,
  X,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import MysticBackground from "@/components/effects/MysticBackground";

const NAV = [
  { to: "/admin",                label: "Dashboard",       icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/admin/pagos",          label: "Aprobar pagos",   icon: Wallet,          testid: "nav-payments",  badge: "pending" },
  { to: "/admin/usuarios",       label: "Usuarios",        icon: Users,           testid: "nav-users" },
  { to: "/admin/transacciones",  label: "Transacciones",   icon: Receipt,         testid: "nav-transactions" },
  { to: "/admin/paquetes",       label: "Paquetes & Planes", icon: Package,       testid: "nav-packages" },
  { to: "/admin/conversaciones", label: "Conversaciones",  icon: MessageSquare,   testid: "nav-conversations" },
  { to: "/admin/urls",           label: "URLs escaneadas", icon: Link2,           testid: "nav-urls" },
  { to: "/admin/creditos",       label: "Auditoría créditos", icon: Coins,        testid: "nav-credits" },
  { to: "/admin/anuncios",       label: "Anuncios",        icon: Megaphone,       testid: "nav-broadcast" },
  { to: "/admin/fraude",         label: "Anti-fraude",     icon: ShieldAlert,     testid: "nav-fraud" },
  { to: "/admin/actividad",      label: "Actividad / IPs", icon: MapPin,          testid: "nav-activity" },
  { to: "/admin/osint",          label: "OSINT Usage",     icon: Radar,           testid: "nav-osint" },
  { to: "/admin/ajustes",        label: "Ajustes del bot", icon: Settings,        testid: "nav-settings" },
];

function SidebarContent({ admin, logout, pending, onItemClick }) {
  return (
    <>
      {/* Brand block — Cinzel Decorative for that gothic mystical feel */}
      <div className="h-20 flex items-center px-5 border-b border-gold-antique/10 flex-shrink-0 relative">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 flex items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-gradient-to-br from-gold-antique/40 via-transparent to-violet-mystic/40 blur-md" />
            <span className="absolute inset-[3px] rounded-full border border-gold-antique/50 bg-black" />
            <Sparkles size={16} className="relative text-gold-antique animate-gold-pulse" strokeWidth={1.8} />
          </div>
          <div className="leading-tight">
            <div className="font-topsecret text-xl tracking-[0.14em] backlight-gold neon-flicker">
              IARISK
            </div>
            <div className="font-bingbam italic text-xs text-violet-mystic/85 mt-0.5">
              Grimoire
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto custom-scroll">
        <div className="text-[10px] uppercase text-gold-antique/60 tracking-[0.3em] font-mono px-3 mb-3 flex items-center gap-2">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-antique/25 to-transparent" />
          Operación
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-gold-antique/25 to-transparent" />
        </div>
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={onItemClick}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all overflow-hidden ${
                  isActive
                    ? "text-white bg-gradient-to-r from-violet-mystic/25 via-violet-mystic/10 to-transparent"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.03]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span
                      layoutId="nav-active-bar"
                      className="absolute left-0 top-1 bottom-1 w-[3px] bg-gradient-to-b from-gold-antique via-gold-pale to-gold-antique rounded-r"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <Icon
                    size={17}
                    strokeWidth={1.8}
                    className={
                      isActive
                        ? "text-gold-antique drop-shadow-[0_0_8px_rgba(212,175,55,0.55)]"
                        : "text-zinc-500 group-hover:text-violet-mystic transition-colors"
                    }
                  />
                  <span className={`font-medium tracking-wide ${isActive ? "text-white" : ""}`}>
                    {item.label}
                  </span>
                  {item.badge === "pending" && pending > 0 && (
                    <span
                      className="ml-auto text-[10px] font-mono font-bold bg-gold-antique/15 text-gold-antique border border-gold-antique/40 rounded-sm px-1.5 py-0.5 shadow-[0_0_10px_rgba(212,175,55,0.35)]"
                      data-testid="sidebar-pending-badge"
                    >
                      {pending}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Admin identity */}
      <div className="p-4 border-t border-gold-antique/10 flex-shrink-0 bg-black/40">
        <div className="flex items-center gap-3 mb-3">
          <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-violet-deep to-black border border-gold-antique/40 flex items-center justify-center text-sm font-runic font-bold text-gold-antique">
            {(admin?.email || "A")[0].toUpperCase()}
            <span className="absolute -inset-0.5 rounded-full border border-gold-antique/25 animate-gold-pulse pointer-events-none" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate text-zinc-100">
              {admin?.name || "Admin"}
            </div>
            <div className="text-[10px] text-zinc-500 truncate font-mono">
              {admin?.email}
            </div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          data-testid="logout-button"
          className="w-full justify-start text-zinc-400 hover:text-red-300 hover:bg-red-950/40 rounded-md border border-transparent hover:border-red-700/40 transition-colors"
        >
          <LogOut size={14} className="mr-2" />
          Cerrar sesión
        </Button>
      </div>
    </>
  );
}

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const [pending, setPending] = useState(0);
  const [botUsername, setBotUsername] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("/stats/dashboard");
        setPending(r.data.pending_payments || 0);
      } catch (e) {
        console.error("[AdminLayout] error:", e);
      }
    };
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [location.pathname]);

  useEffect(() => {
    api.get("/settings").then((r) => setBotUsername(r.data.bot_username)).catch(() => {});
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <div className="min-h-[100dvh] flex text-zinc-100 relative overflow-hidden">
      {/* Ambient background — deep obsidian + gold/violet glows + embers */}
      <MysticBackground />

      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex w-64 flex-shrink-0 flex-col relative z-10 bg-black/70 backdrop-blur-2xl border-r border-gold-antique/10"
        data-testid="admin-sidebar"
      >
        {/* Vertical ethereal gold line */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-transparent via-gold-antique/30 to-transparent" />
        <SidebarContent admin={admin} logout={logout} pending={pending} />
      </aside>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[60] lg:hidden"
          data-testid="drawer-overlay"
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`fixed inset-y-0 left-0 w-72 max-w-[85vw] bg-black/95 backdrop-blur-2xl border-r border-gold-antique/20 flex flex-col z-[70] lg:hidden transition-transform duration-200 ease-out ${
          drawerOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-sidebar"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <button
          onClick={() => setDrawerOpen(false)}
          aria-label="Cerrar menú"
          data-testid="drawer-close-btn"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 border border-gold-antique/30 hover:border-gold-antique text-gold-antique flex items-center justify-center transition-colors"
        >
          <X size={18} />
        </button>
        <SidebarContent
          admin={admin}
          logout={logout}
          pending={pending}
          onItemClick={() => setDrawerOpen(false)}
        />
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <header
          className="sticky top-0 z-50 h-14 sm:h-16 bg-black/60 backdrop-blur-2xl border-b border-gold-antique/10 flex items-center px-3 sm:px-6 lg:px-8 gap-3 relative"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          {/* Subtle bottom gold glow line */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold-antique/40 to-transparent" />

          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden w-9 h-9 rounded-md bg-black/60 border border-gold-antique/25 hover:border-gold-antique text-gold-antique flex items-center justify-center flex-shrink-0 transition-colors"
            aria-label="Abrir menú"
            data-testid="drawer-open-btn"
          >
            <Menu size={18} />
          </button>

          <div className="flex-1 min-w-0">
            <div className="text-[11px] sm:text-xs truncate flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)] animate-pulse" />
              {botUsername ? (
                <>
                  <span className="text-zinc-500 uppercase tracking-widest font-mono">Bot activo:</span>{" "}
                  <span className="text-gold-antique font-mono">@{botUsername}</span>
                </>
              ) : (
                <span className="text-zinc-500 uppercase tracking-widest font-mono">
                  Invocando bot…
                </span>
              )}
            </div>
          </div>
          <Link
            to="/admin/pagos"
            className="relative p-2 rounded-md border border-transparent hover:border-gold-antique/30 hover:bg-black/40 transition-colors flex-shrink-0"
            data-testid="topbar-notifications-bell"
          >
            <Bell size={18} className="text-zinc-400" />
            {pending > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-gold-antique text-[10px] font-mono font-bold text-black flex items-center justify-center shadow-[0_0_10px_rgba(212,175,55,0.7)]">
                {pending > 9 ? "9+" : pending}
              </span>
            )}
          </Link>
        </header>

        <motion.main
          key={location.pathname}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden relative"
          data-testid="admin-main"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
        >
          <Outlet />
        </motion.main>
      </div>
    </div>
  );
}
